import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';

import {
  makeStyles,
  Typography,
  Card,
  Box,
  Container,
  Link,
  Button
} from '@material-ui/core';

import Page from 'src/shared/ui/components/Page';
import Form from 'src/shared/ui/components/Form';
import processFormErrorMessages from 'src/shared/ui/components/Form/FormErrorMessage';

import validateSchema from 'src/shared/models/SchemaValidator';
import Inventory from 'src/inventory/models/Inventory';

import createInventory from 'src/inventory/use-cases/createInventory';
import updateInventoryById from 'src/inventory/use-cases/updateInventoryById';
import getInventoryById from 'src/inventory/use-cases/getInventoryById';

import getProductTypeList from 'src/product-type/use-cases/getProductTypeList';
import getProductCategoryList from 'src/product-category/use-cases/getProductCategoryList';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  saveButton: {
    top: '-20px'
  },
  backButton: {
    top: '-20px',
    marginRight: theme.spacing(1)
  },
  scrollBox: {
    overflow: 'scroll',
    height: '80vh'
  }
}));

const InventoryFormView = () => {
  const user = JSON.parse(localStorage.user);
  const companyHeadquarterId = localStorage.selectedUserHeadquarter;
  const classes = useStyles();
  const [formFields, setFormFields] = useState([]);
  const [isRequesting, setIsRequesting] = useState(false);
  const { inventoryId } = useParams();
  const navigate = useNavigate();

  const initFormFields = async (fields) => {
    const productTypeList = await getProductTypeList({
      page: null,
      limit: null,
      orderBy: ''
    });
    const productCategoryList = await getProductCategoryList({
      page: null,
      limit: null,
      orderBy: ''
    });
    const inventory = Inventory({
      fields,
      user,
      productTypeList: productTypeList.rows,
      productCategoryList,
      userHeadquarterList: JSON.parse(localStorage.userHeadquarterList),
      selectedUserHeadquarter: localStorage.selectedUserHeadquarter
    });

    setFormFields(inventory);
  };

  const fetchForm = async () => {
    const inventory = await getInventoryById(inventoryId);

    initFormFields(inventory);
  };

  const getInitFormFields = () => {
    return { units: 1, location: 'stock', expirationDate: '9999/01/01' };
  };

  useEffect(() => {
    if (inventoryId === 'new') {
      initFormFields(getInitFormFields());
    } else {
      fetchForm();
    }
  }, []);

  const validateFormFields = async (formFieldsValues) => {
    const validatedFormFields = await validateSchema(formFieldsValues);
    formFieldsValues = processFormErrorMessages(
      validatedFormFields,
      formFieldsValues
    );
    setFormFields(formFieldsValues.fields);

    return {
      errors: formFieldsValues.errors,
      fields: validatedFormFields
    };
  };

  const handleChangeFormFieldsValues = (formFieldsValues, target) => {
    if (target.name === 'productTypeId') {
      let fields = getInitFormFields();
      fields.productTypeId = target.value;

      initFormFields(fields);

      return;
    }

    setFormFields(formFieldsValues);
  };

  const postForm = async () => {
    if (isRequesting) {
      return;
    }

    const validatedForm = await validateFormFields(formFields);
    if (validatedForm.errors) {
      alert('Rellene todos los campos correctamente.');
      return;
    }

    setIsRequesting(true);

    let result;
    if (inventoryId === 'new') {
      validatedForm.fields.companyHeadquarterId = companyHeadquarterId;
      result = await createInventory(validatedForm.fields);
    } else {
      result = await updateInventoryById(validatedForm.fields, inventoryId);
    }

    setIsRequesting(false);

    if (typeof result.id === 'undefined') {
      alert('Se ha producido un error, vuelva a intentarlo');
    } else {
      navigate('/app/inventory');
    }
  };

  return (
    <Page className={classes.root} title="Inventario">
      <Container maxWidth={false}>
        <Box className={classes.navigation} display="flex">
          <Typography color="textSecondary" variant="body2">
            <Link component={RouterLink} to="/app/inventory">
              Inventario
            </Link>
            {` >  ${
              formFields.length &&
              formFields[6].value &&
              formFields[6].value.length
                ? formFields[6].value
                : 'Nuevo'
            }`}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="flex-end">
          <Link component={RouterLink} to="/app/inventory">
            <Button className={classes.backButton}>{'Volver a listado'}</Button>
          </Link>
          <Button
            color="primary"
            variant="contained"
            className={classes.saveButton}
            onClick={postForm}
          >
            Guardar
          </Button>
        </Box>
        <Card>
          <Form
            fields={formFields}
            onChangeFields={handleChangeFormFieldsValues}
          />
        </Card>
      </Container>
    </Page>
  );
};

export default InventoryFormView;
